import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeedbackModalStyles as styles } from '../css/styles';
import api from '../services/api';

type Category = 'bug' | 'translation' | 'improvement';
type Step = 'categories' | 'form' | 'success';

interface Props {
    onClose: () => void;
}

const CATEGORIES: { key: Category; icon: string; labelKey: string }[] = [
    { key: 'bug',         icon: '🐛', labelKey: 'feedback.bug' },
    { key: 'translation', icon: '🌐', labelKey: 'feedback.translation' },
    { key: 'improvement', icon: '💡', labelKey: 'feedback.improvement' },
];

const FeedbackModal: React.FC<Props> = ({ onClose }) => {
    const { t } = useTranslation();
    const [step, setStep] = useState<Step>('categories');
    const [category, setCategory] = useState<Category | null>(null);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleCategorySelect = (cat: Category) => {
        setCategory(cat);
        setStep('form');
    };

    const handleSubmit = async () => {
        if (!message.trim() || !category) return;
        setLoading(true);
        setError('');
        try {
            await api.post('/error/feedback', { category, message });
            setStep('success');
        } catch {
            setError(t('feedback.error'));
        } finally {
            setLoading(false);
        }
    };

    const selectedCategory = CATEGORIES.find(c => c.key === category);

    const placeholderKey: Record<Category, string> = {
        bug:         'feedback.placeholderBug',
        translation: 'feedback.placeholderTranslation',
        improvement: 'feedback.placeholderImprovement',
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
                <button className={styles.close} onClick={onClose} aria-label="Close">✕</button>

                {step === 'categories' && (
                    <>
                        <h2 className={styles.title}>{t('feedback.title')}</h2>
                        <div className={styles.categories}>
                            {CATEGORIES.map(cat => (
                                <button
                                    key={cat.key}
                                    className={styles.categoryBtn}
                                    onClick={() => handleCategorySelect(cat.key)}
                                >
                                    <span className={styles.categoryIcon}>{cat.icon}</span>
                                    <span>{t(cat.labelKey)}</span>
                                </button>
                            ))}
                        </div>
                    </>
                )}

                {step === 'form' && selectedCategory && (
                    <>
                        <button className={styles.back} onClick={() => setStep('categories')}>
                            ← {t('feedback.back')}
                        </button>
                        <h2 className={styles.title}>{t('feedback.title')}</h2>
                        <div className={styles.selectedCategory}>
                            <span>{selectedCategory.icon}</span>
                            <span>{t(selectedCategory.labelKey)}</span>
                        </div>
                        <textarea
                            className={styles.textarea}
                            placeholder={category ? t(placeholderKey[category]) : ''}
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                            rows={5}
                            maxLength={2000}
                            autoFocus
                        />
                        {error && <p className={styles.error}>{error}</p>}
                        <button
                            className={styles.submitBtn}
                            onClick={handleSubmit}
                            disabled={!message.trim() || loading}
                        >
                            {loading ? '...' : t('feedback.submit')}
                        </button>
                    </>
                )}

                {step === 'success' && (
                    <div className={styles.success}>
                        <span className={styles.successIcon}>✓</span>
                        <p>{t('feedback.success')}</p>
                        <button className={styles.submitBtn} onClick={onClose}>
                            {t('feedback.close')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FeedbackModal;
